import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { STRAINS, TERPENES_LIST, getCbdDisplay } from "../lib/strainsData";
import { useLanguage } from "../lib/LanguageContext";
import { translateTerpeneName, translateGenetics, translateAromas, translateEffect } from "../lib/dataTranslations";
import { usePageTitle } from "../hooks/usePageTitle";
import Breadcrumb from "../components/Breadcrumb";
import { ArrowLeft, ArrowLeftRight, Search, X, Plus } from "lucide-react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger
} from "@/components/ui/drawer";
import TerpeneDifferenceView from "../components/TerpeneDifferenceView";
import StrainVapeCompareTable from "../components/StrainVapeCompareTable";
import ShareComparisonButton from "../components/ShareComparisonButton";
import { TERPENES } from "../lib/terpenesData";
import StrainPairSelectors from "../components/StrainPairSelectors";
import StrainPairComparison from "../components/StrainPairComparison";
import '@/components/mobile-comparison.css';

const MAX_COMPARE = 5;
const COLOR_HEX = ["#2d6a4f", "#e9c46a", "#4a6fa5", "#bc4749", "#6d597a"];

// Two most recently added strains — used as the default comparison.
const NEWEST_TWO_IDS = [...STRAINS]
  .sort((a, b) => (b.dateAdded || "").localeCompare(a.dateAdded || ""))
  .slice(0, 2)
  .map((s) => s.id);

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const terpeneLabel = (t, lang) => translateTerpeneName(capitalize(t), lang) || t;

function AddStrainButton({ strains, onAdd, placeholder, language }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    if (!searchQuery) return strains;
    const q = searchQuery.toLowerCase();
    return strains.filter(
      (s) => s.name.toLowerCase().includes(q) || s.producer.toLowerCase().includes(q)
    );
  }, [strains, searchQuery]);

  return (
    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
      <DrawerTrigger asChild>
        <button className="flex-1 min-w-[140px] flex items-center justify-center gap-2 text-sm pl-3 pr-3 py-2.5 min-h-[64px] rounded-xl border border-dashed border-border bg-background hover:border-primary/40 hover:text-primary transition-colors text-muted-foreground">
          <Plus className="w-4 h-4" />
          {placeholder}
        </button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle>{language === "pl" ? "Dodaj odmianę" : "Add strain"}</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={language === "pl" ? "Szukaj odmiany..." : "Search strains..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoFocus
            />
          </div>
        </div>
        <div className="overflow-y-auto px-4 pb-8 space-y-1 max-h-[55vh]">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                onAdd(s.id);
                setDrawerOpen(false);
                setSearchQuery("");
              }}
              className="w-full flex items-center justify-between px-3 py-3 min-h-[52px] rounded-xl hover:bg-accent text-left transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground truncate">{s.producer}</p>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                THC {s.thc}%
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              {language === "pl" ? "Brak wyników" : "No results"}
            </p>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function StrainCompare() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIds = useMemo(() => {
    const fromUrl = (searchParams.get("ids") || "")
      .split(",")
      .filter((id) => STRAINS.some((s) => s.id === id))
      .slice(0, MAX_COMPARE);
    return searchParams.has("ids") ? fromUrl : NEWEST_TWO_IDS;
  }, [searchParams]);
  const setSelectedIds = (next) => {
    const ids = typeof next === "function" ? next(selectedIds) : next;
    setSearchParams({ ids: ids.join(",") }, { replace: true });
  };

  usePageTitle(
    language === "pl" ? "Porównanie odmian" : "Strain Comparison",
    language === "pl"
      ? "Porównaj profile terpenowe odmian medycznej marihuany"
      : "Compare terpene profiles of medical cannabis strains"
  );

  const selected = useMemo(
    () => selectedIds.map((id) => STRAINS.find((s) => s.id === id)).filter(Boolean),
    [selectedIds]
  );
  const available = useMemo(
    () => STRAINS.filter((s) => !selectedIds.includes(s.id)),
    [selectedIds]
  );
  const colors = selected.map((_, i) => COLOR_HEX[i % COLOR_HEX.length]);

  const addStrain = (id) => {
    if (id && !selectedIds.includes(id) && selectedIds.length < MAX_COMPARE) {
      setSelectedIds([...selectedIds, id]);
    }
  };
  const removeStrain = (id) => setSelectedIds((prev) => prev.filter((x) => x !== id));

  const bothSelected = selected.length >= 2;

  // Track every comparison of 2+ strains (only when the user picked them via URL/UI).
  useEffect(() => {
    if (bothSelected && searchParams.has("ids")) {
      base44.analytics.track({
        eventName: "strains_compared",
        properties: { count: selectedIds.length, ids: selectedIds.join(",") },
      });
    }
  }, [selectedIds.join(",")]);

  // Active terpenes: present in any selected strain
  const activeTerpenes = useMemo(
    () =>
      selected.length > 0
        ? TERPENES_LIST.filter((t) => selected.some((s) => (s.terpenes[t] || 0) > 0))
        : [],
    [selected]
  );

  // Radar data
  const radarData = useMemo(
    () =>
      activeTerpenes.map((t) => {
        const row = { terpene: terpeneLabel(t, language) };
        selected.forEach((s) => { row[s.name] = s.terpenes[t] || 0; });
        return row;
      }),
    [activeTerpenes, selected, language]
  );

  // Bar data (sorted by total across selected)
  const barData = useMemo(
    () =>
      activeTerpenes
        .map((t) => {
          const row = { terpene: terpeneLabel(t, language) };
          selected.forEach((s) => { row[s.name] = s.terpenes[t] || 0; });
          return row;
        })
        .sort(
          (a, b) =>
            selected.reduce((sum, s) => sum + (b[s.name] || 0), 0) -
            selected.reduce((sum, s) => sum + (a[s.name] || 0), 0)
        ),
    [activeTerpenes, selected, language]
  );

  const dominant = (s) =>
    TERPENES_LIST.reduce(
      (max, t) => ((s.terpenes[t] || 0) > (s.terpenes[max] || 0) ? t : max),
      TERPENES_LIST[0]
    );

  // Recommended vaporization temperature (same logic as VaporizationCalculator):
  // min 180°C (therapeutic baseline), max = highest terpene boiling point + 5°C, capped at 220°C
  const getVapeRange = (strain) => {
    if (!strain) return null;
    const bps = Object.keys(strain.terpenes)
      .map((sn) => TERPENES.find((t) => t.shortName.toLowerCase() === sn.toLowerCase()))
      .filter((t) => t && t.boilingPoint)
      .map((t) => t.boilingPoint);
    if (bps.length === 0) return null;
    return { min: 180, max: Math.min(220, Math.round(Math.max(...bps)) + 5) };
  };

  const vapeItems = selected.map((s, i) => ({
    name: s.name,
    color: colors[i],
    vape: getVapeRange(s),
  }));

  const gridCols = "strain-comparison-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === "pl" ? "Odmiany" : "Strains"}
      </Link>

      <div className="mb-8 text-center">
        <div className="flex flex-wrap items-center justify-center gap-3 mb-2">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-bold">
            {language === "pl" ? "Porównanie odmian" : "Strain Comparison"}
          </h1>
          <ShareComparisonButton ids={selectedIds} />
        </div>
        <p className="text-muted-foreground">
          {language === "pl"
            ? `Wybierz do ${MAX_COMPARE} odmian i porównaj ich profile terpenowe`
            : `Select up to ${MAX_COMPARE} strains and compare their terpene profiles`}
        </p>
      </div>

      {/* Cross-link to terpene comparison + share */}
      <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
        <Link
          to="/porownanie-terpenow"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeftRight className="w-4 h-4" />
          {language === "pl" ? "Porównaj terpeny" : "Compare terpenes"}
        </Link>
      </div>

      {/* Selectors */}
      <div className="strain-comparison-selectors flex items-stretch gap-2 mb-8 flex-wrap justify-center">
        {selected.map((s, i) => (
          <div
            key={s.id}
            className="flex-1 min-w-[140px] rounded-xl border bg-card p-3 flex items-center gap-2"
            style={{ borderColor: colors[i] + "60" }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate" style={{ color: colors[i] }}>
                {s.name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{s.producer}</p>
            </div>
            <button
              onClick={() => removeStrain(s.id)}
              aria-label={language === "pl" ? "Usuń z porównania" : "Remove from comparison"}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {selected.length < MAX_COMPARE && (
          <AddStrainButton
            strains={available}
            onAdd={addStrain}
            placeholder={language === "pl" ? "Dodaj odmianę" : "Add strain"}
            language={language}
          />
        )}
      </div>

      <StrainPairSelectors
        ids={selectedIds.slice(0, 2)}
        onChange={(next) => setSelectedIds([...next.filter(Boolean), ...selectedIds.slice(2).filter((id) => !next.includes(id))])}
        language={language}
      />
      {bothSelected && <StrainPairComparison strains={selected.slice(0, 2)} language={language} />}

      {/* Strain info cards */}
      {selected.length > 0 && (
        <div className={`${gridCols} mb-6`}>
          {selected.map((s, i) => (
            <Link
              key={s.id}
              to={`/odmiana/${s.id}`}
              className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors"
            >
              <h2 className="font-playfair font-bold text-base md:text-lg truncate" style={{ color: colors[i] }}>
                {s.name}
              </h2>
              <p className="text-xs text-muted-foreground truncate mb-2">{s.producer}</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent border border-border text-accent-foreground">
                  {translateGenetics(s.genetics, language)}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent border border-border text-accent-foreground">
                  THC {s.thc}%
                </span>
                {s.cbd > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent border border-border text-accent-foreground">
                    CBD {getCbdDisplay(s)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Summary stats */}
      {selected.length > 0 && (
        <div className={`${gridCols} mb-6`}>
          {selected.map((s, i) => (
            <div key={s.id} className="bg-card rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground mb-2">
                {language === "pl" ? "Profil terpenowy" : "Terpene profile"}
              </p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{language === "pl" ? "Dominujący" : "Dominant"}</span>
                  <span className="font-medium" style={{ color: colors[i] }}>{terpeneLabel(dominant(s), language)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vaporization temperature comparison table */}
      {bothSelected && vapeItems.some((v) => v.vape) && (
        <StrainVapeCompareTable items={vapeItems} />
      )}

      {/* Radar chart */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <h3 className="font-semibold mb-4 text-center">
          {language === "pl" ? "Radar profilu terpenowego" : "Terpene Profile Radar"}
        </h3>
        {bothSelected && activeTerpenes.length > 0 ? (
          <ResponsiveContainer width="100%" height={340}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="terpene" tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [`${value}%`]} />
              {selected.map((s, i) => (
                <Radar
                  key={s.id}
                  name={s.name}
                  dataKey={s.name}
                  stroke={colors[i]}
                  fill={colors[i]}
                  fillOpacity={0.18}
                />
              ))}
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            {language === "pl"
              ? "Wybierz co najmniej dwie odmiany powyżej, aby zobaczyć porównanie"
              : "Select at least two strains above to see the comparison"}
          </p>
        )}
      </div>

      {/* Terpene differences */}
      {bothSelected && (
        <div className="bg-card rounded-xl border border-border p-5 mb-6">
          <h3 className="font-semibold mb-1">
            {language === "pl" ? "Różnice w profilu terpenowym" : "Terpene profile differences"}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {language === "pl"
              ? "Terpeny uporządkowane od największych różnic — pomaga dobrać susz pod konkretny efekt."
              : "Terpenes sorted by largest differences — helps pick the right flower for a desired effect."}
          </p>
          <TerpeneDifferenceView strains={selected} colors={colors} />
        </div>
      )}

      {/* Bar chart */}
      {bothSelected && activeTerpenes.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-5 mb-6">
          <h3 className="font-semibold mb-1">
            {language === "pl" ? "Szczegółowe zestawienie" : "Detailed breakdown"}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {language === "pl" ? "Zawartość poszczególnych terpenów (%)" : "Individual terpene content (%)"}
          </p>
          <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 36)}>
            <BarChart data={barData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="terpene" tick={{ fontSize: 10 }} width={80} />
              <Tooltip formatter={(v) => [`${v}%`]} />
              <Legend />
              {selected.map((s, i) => (
                <Bar key={s.id} dataKey={s.name} fill={colors[i]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Aromas comparison */}
      {selected.length > 0 && (
        <div className={`${gridCols} mb-6`}>
          {selected.map((s, i) => (
            <div key={s.id} className="bg-card rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground mb-2" style={{ color: colors[i] }}>
                {language === "pl" ? "Aromaty" : "Aromas"}
              </p>
              <div className="flex flex-wrap gap-1">
                {translateAromas(s.aromas || [], language).map((a) => (
                  <span key={a} className="text-xs px-2 py-0.5 rounded-full bg-accent border border-border text-accent-foreground">
                    {a}
                  </span>
                ))}
              </div>
              {s.effect && (
                <>
                  <p className="text-xs text-muted-foreground mt-3 mb-1" style={{ color: colors[i] }}>
                    {language === "pl" ? "Efekt" : "Effect"}
                  </p>
                  <p className="text-sm">{translateEffect(s.effect, language)}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}